import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { client } from 'cccisd-apollo';
import _get from 'lodash/get';

import getYouthListSupervisorQuery from './getYouthListSupervisor.graphql';
import { setSelectedCareCoordinator } from '../SmartWrapDashboard/dashboardSlice';

const initialState = {
    loading: true,
    youthList: [],
    filteredYouthList: [],
    careCoordinatorList: [],
    youthListFetched: false,
};

const applyYouthFilter = (youthList, careCoordinatorList, selectedCCPawnId) => {
    const supervisorCCPawnIds = new Set(careCoordinatorList.map(cc => cc.pawn.pawnId));
    return youthList.filter(youth => {
        const youthCCs = _get(youth, 'parentRoles.careCoordinatorList', []);
        if (!youthCCs.some(cc => supervisorCCPawnIds.has(cc.pawn.pawnId))) return false;
        if (selectedCCPawnId) {
            return youthCCs.some(cc => cc.pawn.pawnId === selectedCCPawnId);
        }
        return true;
    });
};

export const querySupervisorYouthList = createAsyncThunk('supervisor/queryYouthList', async () => {
    const response = await client.query({
        query: getYouthListSupervisorQuery,
        fetchPolicy: 'network-only',
    });

    const careCoordinatorList = _get(response, 'data.roles.careCoordinatorList', []);

    const youthList = _get(response, 'data.roles.youthList', []).map(youth => {
        const fields = { ...youth.fields };
        fields.flags = [];

        let youthPlanDeploymentIds = {};

        youth.selectedAssignmentPlanList.forEach(plan => {
            youthPlanDeploymentIds[plan.assignmentPlanId] = {
                sessionList: plan.sessionList.map(session => session.sessionId),
                settings: JSON.parse(plan.settings),
            };
        });

        youth.flags.forEach(flag => {
            const flagParentPlan =
                Object.values(youthPlanDeploymentIds).find(plan => plan.sessionList.includes(flag.sessionId)) || {};

            const planName = _get(flagParentPlan, 'settings.planName', '');
            const planStartDate = _get(flagParentPlan, 'settings.startDate', '');

            flag.flagProgressList.forEach(progress => {
                const matchVariableName = progress.flag.equation?.match(/@(\w+)@/);
                const variableName = matchVariableName ? matchVariableName[1] : null;
                const variableValue = (variableName && flag.variables?.[variableName]) ?? progress.flag.equation;

                fields.flags.push({
                    key: `${youth.pawn.pawnId}_${progress.flagId}`,
                    respondent_pawn_id: youth.pawn.pawnId,
                    critical: progress.flag.name.toLowerCase().includes('critical'),
                    surveyItem: progress.flag.description,
                    response: variableValue,
                    date: progress.flaggedDate,
                    respondent: 'youth',
                    actions: ['resolve', 'addNote', 'viewLogs'],
                    log: progress.log,
                    notes: progress.notes,
                    flagId: progress.flagId,
                    status: progress.status,
                    unflaggedReason: progress.unflaggedReason,
                    flagProgressId: progress.flagProgressId,
                    unflaggedAt: progress.unflaggedAt,
                    wrapId: youth.fields.wrapId,
                    sessionId: flag.sessionId,
                    planName,
                    planStartDate,
                });
            });
        });

        youth.parentRoles.caregiverList.forEach(caregiver => {
            caregiver.flags.forEach(flag => {
                const sessionId = flag.sessionId;
                if (
                    !Object.values(youthPlanDeploymentIds)
                        .map(plan => plan.sessionList)
                        .flat()
                        .includes(sessionId)
                )
                    return;

                const flagParentPlan =
                    Object.values(youthPlanDeploymentIds).find(plan => plan.sessionList.includes(flag.sessionId)) || {};

                const planName = _get(flagParentPlan, 'settings.planName', '');
                const planStartDate = _get(flagParentPlan, 'settings.startDate', '');

                flag.flagProgressList.forEach(progress => {
                    const matchVariableName = progress.flag.equation?.match(/@(\w+)@/);
                    const variableName = matchVariableName ? matchVariableName[1] : null;
                    const variableValue = (variableName && flag.variables?.[variableName]) ?? progress.flag.equation;

                    fields.flags.push({
                        key: `${caregiver.pawn.pawnId}_${progress.flagId}`,
                        respondent_pawn_id: caregiver.pawn.pawnId,
                        critical: progress.flag.name.toLowerCase().includes('critical'),
                        surveyItem: progress.flag.description,
                        response: variableValue,
                        date: progress.flaggedDate,
                        respondent: 'caregiver',
                        caregiverName: caregiver.fields.caregiverName,
                        actions: ['resolve', 'addNote', 'viewLogs'],
                        log: progress.log,
                        notes: progress.notes,
                        flagId: progress.flagId,
                        status: progress.status,
                        unflaggedReason: progress.unflaggedReason,
                        flagProgressId: progress.flagProgressId,
                        unflaggedAt: progress.unflaggedAt,
                        wrapId: youth.fields.wrapId,
                        sessionId,
                        planName,
                        planStartDate,
                    });
                });
            });
        });

        return { ...youth, flags: fields.flags };
    });

    return { youthList, careCoordinatorList };
});

const supervisorSlice = createSlice({
    name: 'supervisor',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(querySupervisorYouthList.pending, state => {
                state.loading = true;
            })
            .addCase(querySupervisorYouthList.fulfilled, (state, action) => {
                state.youthList = action.payload.youthList;
                state.careCoordinatorList = action.payload.careCoordinatorList;
                state.filteredYouthList = applyYouthFilter(
                    action.payload.youthList,
                    action.payload.careCoordinatorList,
                    null
                );
                state.youthListFetched = true;
                state.loading = false;
            })
            .addCase(querySupervisorYouthList.rejected, state => {
                state.loading = false;
            })
            .addCase(setSelectedCareCoordinator, (state, action) => {
                state.filteredYouthList = applyYouthFilter(state.youthList, state.careCoordinatorList, action.payload);
            });
    },
});

export default supervisorSlice.reducer;
